import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import BottomTabNav from "../../components/BottomTabNav";
import { Heart, Volume2, Trash2, Luggage, MessageCircle, RefreshCw } from "lucide-react";

const FavoritesPage = () => {
  const { t, i18n } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  
  const isRTL = i18n.language === 'ar';

  // Localized dialect labels
  const getDialectLabel = (dialectCode) => {
    const dialectKeys = {
      "NEUTRAL": "favorites.dialects.neutral",
      "DO": "favorites.dialects.dominican",
      "MX": "favorites.dialects.mexican",
      "ES": "favorites.dialects.spain",
      "CO": "favorites.dialects.colombian",
      "AR": "favorites.dialects.argentine"
    };
    const dialectFlags = {
      "NEUTRAL": "🌎",
      "DO": "🇩🇴",
      "MX": "🇲🇽",
      "ES": "🇪🇸",
      "CO": "🇨🇴",
      "AR": "🇦🇷"
    };
    return {
      flag: dialectFlags[dialectCode] || "🌎",
      label: t(dialectKeys[dialectCode] || dialectKeys["NEUTRAL"])
    };
  };

  // Localized scenario labels
  const getScenarioLabel = (scenario) => {
    const scenarioMap = {
      "emergency": { icon: "🚨", key: "favorites.scenarios.emergency" },
      "food": { icon: "🍽️", key: "favorites.scenarios.food" },
      "date": { icon: "💕", key: "favorites.scenarios.date" },
      "directions": { icon: "🧭", key: "favorites.scenarios.directions" },
      "hotel": { icon: "🏨", key: "favorites.scenarios.hotel" },
      "general": { icon: "💬", key: "favorites.scenarios.general" }
    };
    const info = scenarioMap[scenario];
    if (!info) return null;
    return `${info.icon} ${t(info.key)}`;
  };

  // Localized language names
  const getLanguageName = (langCode) => {
    const langKey = `favorites.languages.${langCode}`;
    return t(langKey, langCode.toUpperCase());
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    setLoading(true);
    setError(false);
    
    try {
      const response = await api.get('/favorites');
      setFavorites(response.data || []);
    } catch (err) {
      console.error("Favorites error:", err);
      setError(true);
      
      if (err.code === 'ECONNABORTED') {
        toast.error(t('favorites.timeout'));
      } else if (err.response?.status === 401) {
        toast.error(t('favorites.sessionExpired'));
      } else {
        toast.error(t('toast.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSpeak = (text, targetLang, favoriteId) => {
    // Stop any current speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Map language codes to speech synthesis locales
    const langMap = {
      'en': 'en-US',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'ar': 'ar-SA'
    };
    utterance.lang = langMap[targetLang] || 'es-ES';
    
    utterance.onstart = () => setSpeakingId(favoriteId);
    utterance.onend = () => setSpeakingId(null);
    utterance.onerror = () => setSpeakingId(null);
    
    window.speechSynthesis.speak(utterance);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/favorites/${id}`);
      setFavorites(favorites.filter(f => f.id !== id));
      toast.success(t('toast.removedFromFavorites'));
    } catch (error) {
      toast.error(t('toast.failedToDelete'));
    }
  };

  return (
    <div 
      className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="max-w-2xl mx-auto">
        <h1 
          className="text-4xl font-bold text-travel-navy font-outfit mb-8" 
          data-testid="favorites-page-title"
        >
          {t('favorites.title')}
        </h1>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-aviation border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">{t('favorites.loading')}</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl border-2 border-red-200 shadow-sm p-12 text-center" data-testid="error-state">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h3 className="text-xl font-semibold text-travel-navy mb-2">{t('favorites.failedTitle')}</h3>
            <p className="text-slate-600 mb-6">{t('favorites.failedDesc')}</p>
            <button
              onClick={fetchFavorites}
              className="bg-aviation hover:bg-sky-600 text-white rounded-full px-8 py-3 font-semibold shadow-lg transition-all active:scale-95 flex items-center gap-2 mx-auto"
              data-testid="retry-button"
            >
              <RefreshCw className="w-4 h-4" />
              {t('common.retry')}
            </button>
          </div>
        ) : favorites.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-12 text-center" data-testid="empty-favorites-message">
            <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-travel-navy mb-2">{t('favorites.emptyTitle')}</h3>
            <p className="text-slate-600">{t('favorites.emptyDesc')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map((favorite) => {
              const isConversation = favorite.mode === "conversation";
              const dialectInfo = getDialectLabel(favorite.dialect_country);
              const scenarioLabel = favorite.scenario ? getScenarioLabel(favorite.scenario) : null;
              const sourceLang = favorite.source_lang || 'en';
              const targetLang = favorite.target_lang || 'es';
              const isSpeaking = speakingId === favorite.id;
              
              return (
                <div
                  key={favorite.id}
                  className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 hover:shadow-md transition-all"
                  data-testid={`favorite-item-${favorite.id}`}
                >
                  {/* Mode and metadata tags */}
                  <div className={`flex flex-wrap gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {/* Mode tag */}
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      isConversation 
                        ? "bg-emerald-50 text-emerald-700" 
                        : "bg-sky-50 text-sky-700"
                    }`}>
                      {isConversation ? <MessageCircle className="w-3 h-3" /> : <Luggage className="w-3 h-3" />}
                      {isConversation ? t('favorites.modeWhatsApp') : t('favorites.modeTravel')}
                    </span>
                    
                    {/* Scenario tag (for travel mode) */}
                    {!isConversation && scenarioLabel && (
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                        {scenarioLabel}
                      </span>
                    )}
                    
                    {/* Dialect tag */}
                    {favorite.dialect_country && favorite.dialect_country !== "NEUTRAL" && (
                      <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
                        {dialectInfo.flag} {dialectInfo.label}
                      </span>
                    )}
                  </div>
                  
                  {/* Source text */}
                  <div className="mb-4">
                    <p className={`text-sm text-slate-500 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {getLanguageName(sourceLang)}
                    </p>
                    <p className={`text-travel-navy font-medium ${sourceLang === 'ar' ? 'text-right' : 'text-left'}`} dir={sourceLang === 'ar' ? 'rtl' : 'ltr'}>
                      {favorite.source_text}
                    </p>
                  </div>
                  
                  {/* Translated text */}
                  <div className="border-t border-dashed border-slate-200 pt-4 mb-4">
                    <p className={`text-sm text-slate-500 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {getLanguageName(targetLang)}
                    </p>
                    <p className={`text-travel-navy font-medium ${targetLang === 'ar' ? 'text-right' : 'text-left'}`} dir={targetLang === 'ar' ? 'rtl' : 'ltr'}>
                      {favorite.translated_text}
                    </p>
                  </div>
                  
                  {/* Action buttons */}
                  <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <button
                      onClick={() => handleSpeak(favorite.translated_text, targetLang, favorite.id)}
                      disabled={isSpeaking}
                      className={`flex-1 rounded-full h-10 font-semibold transition-all flex items-center justify-center gap-2 ${
                        isSpeaking 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-sage/30 hover:bg-sage/50 text-travel-navy'
                      }`}
                      data-testid={`speak-button-${favorite.id}`}
                    >
                      <Volume2 className={`w-4 h-4 ${isSpeaking ? 'animate-pulse' : ''}`} />
                      {isSpeaking ? t('common.playing') : t('favorites.play')}
                    </button>
                    <button
                      onClick={() => handleDelete(favorite.id)}
                      className="bg-white border-2 border-slate-200 text-slate-700 hover:bg-red-50 hover:text-red-600 hover:border-red-200 rounded-full px-4 h-10 transition-all flex items-center justify-center"
                      data-testid={`delete-button-${favorite.id}`}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomTabNav />
    </div>
  );
};

export default FavoritesPage;
